import { forwardRef, useMemo } from 'react'
import { Label } from './ui/label'
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'

type UpscalePresetsProps = {
	selectedPreset: string
	onPresetChange: (value: string) => void
	originalDimensions: {
		width?: number
		height?: number
	}
}

const DisabledToggleItem = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	(props, ref) => (
		<div
			ref={ref}
			{...props}
			className={`cursor-not-allowed px-3 py-2 opacity-50 ${props.className || ''}`}
		/>
	),
)

DisabledToggleItem.displayName = 'DisabledToggleItem'

export function UpscalePresets({
	selectedPreset,
	onPresetChange,
	originalDimensions,
}: UpscalePresetsProps) {
	const presets = useMemo(() => {
		const basePresets = [
			{ value: '1.25x', multiplier: 1.25 },
			{ value: '1.5x', multiplier: 1.5 },
			{ value: '1.75x', multiplier: 1.75 },
			{ value: '2x', multiplier: 2 },
			{ value: '2.5x', multiplier: 2.5 },
			{ value: '3x', multiplier: 3 },
			{ value: '4x', multiplier: 4 },
		]

		return basePresets.map(preset => ({
			...preset,
			disabled:
				Math.max(
					(originalDimensions.width || 0) * preset.multiplier,
					(originalDimensions.height || 0) * preset.multiplier,
				) > 4096,
		}))
	}, [originalDimensions])

	return (
		<div>
			<Label className="text-sm font-medium">Presets</Label>
			<ToggleGroup
				type="single"
				value={selectedPreset}
				onValueChange={onPresetChange}
				className="mt-2 flex flex-wrap gap-2"
			>
				<TooltipProvider delayDuration={0}>
					{presets.map(preset =>
						preset.disabled ? (
							<Tooltip key={preset.value}>
								<TooltipTrigger asChild>
									<DisabledToggleItem className="rounded-md bg-gray-100 px-3 py-2 text-sm">
										{preset.value}
									</DisabledToggleItem>
								</TooltipTrigger>
								<TooltipContent side="top" className="px-2 py-1 text-xs">
									Max size: 4096x4096
								</TooltipContent>
							</Tooltip>
						) : (
							<ToggleGroupItem
								key={preset.value}
								value={preset.value}
								className="rounded-md px-3 py-2 text-sm transition-colors data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
							>
								{preset.value}
							</ToggleGroupItem>
						),
					)}
				</TooltipProvider>
			</ToggleGroup>
		</div>
	)
}
